import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from './store/auth.store';
import { useProfileSetup } from './hooks/useProfileSetup';
import { registerForPush, initPush, setPushHandlers } from './services/push';
import { AppLayout } from './components/layout/AppLayout';

// Screens (code-split)
const LoginScreen = React.lazy(() => import('./screens/LoginScreen'));
const VerifyEmailScreen = React.lazy(() => import('./screens/VerifyEmailScreen'));
const RegisterScreen = React.lazy(() => import('./screens/RegisterScreen'));
const OnboardingScreen = React.lazy(() => import('./screens/OnboardingScreen'));
const DashboardScreen = React.lazy(() => import('./screens/DashboardScreen'));
const AssessmentScreen = React.lazy(() => import('./screens/AssessmentScreen'));
const MoodScreen = React.lazy(() => import('./screens/MoodScreen'));
const CompanionScreen = React.lazy(() => import('./screens/CompanionScreen'));
const ContentScreen = React.lazy(() => import('./screens/ContentScreen'));
const CarePlanScreen = React.lazy(() => import('./screens/CarePlanScreen'));
const ProfileScreen = React.lazy(() => import('./screens/ProfileScreen'));
const CrisisScreen = React.lazy(() => import('./screens/CrisisScreen'));
const PrivacyPolicyScreen = React.lazy(() => import('./screens/legal/PrivacyPolicyScreen'));
const TermsScreen = React.lazy(() => import('./screens/legal/TermsScreen'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      onError: (error) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

/**
 * Send a newly registered mother through onboarding once.
 *
 * Sign-up asks only for an email and a password; her name, stage and consents
 * live on the onboarding screen, which was fully built, fully translated, and
 * reachable from nowhere. Every real account therefore landed on a dashboard
 * that had never been told who she was.
 *
 * Asked once, and only once. If she skips, the entry point moves to her
 * profile tab and she is not intercepted again — someone opening this app on
 * a bad night should not have to get past a form first.
 */
function RequireSetup({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { shouldPrompt } = useProfileSetup();

  if (shouldPrompt && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

function RequireGuest({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

const Suspense = ({ children }: { children: React.ReactNode }) => (
  <React.Suspense fallback={
    <div className="loading-screen">
      <div className="loading-spinner" aria-label="Loading" />
    </div>
  }>
    {children}
  </React.Suspense>
);

/** Where tapping a notification should land her. */
function routeForNotification(notifType: string | undefined): string | null {
  switch (notifType) {
    /* The only one worth interrupting her navigation for. She tapped a message
       about being supported through a crisis; the crisis screen is the one with
       the hotlines on it, and it is reachable without authentication. */
    case 'crisis_support':   return '/crisis';
    case 'clinician_concern':
    case 'patient_withdrawn': return '/dashboard';
    /* Unknown or absent type: do nothing rather than guess. Sending her
       somewhere arbitrary is worse than leaving her where she chose to be. */
    default: return null;
  }
}

/**
 * Everything this app does about push, in one place inside the Router.
 *
 * Registration used to live only in login(), so it ran exactly once — for
 * someone typing a password. Every user with a restored session, which is every
 * returning user and at rollout every existing one, stayed unreachable until
 * they happened to sign out and back in. Found on a real handset: signed in,
 * Firebase up, and push_tokens empty. Running it on isAuthenticated covers both
 * the login transition and a session restored at startup, and gives a failed
 * token POST a second chance on the next launch.
 *
 * Safe to repeat: register() is idempotent and the endpoint is ON CONFLICT DO
 * NOTHING, so a returning device re-confirms its row rather than duplicating
 * it. Not a nag either — registerForPush checks the permission before asking,
 * so a device where she declined returns without a prompt.
 *
 * It renders nothing. It is here rather than in App() because routing a tapped
 * notification needs useNavigate, which only works inside the Router.
 */
function PushBridge() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  /* Channels and listeners are not gated on signing in. Channels describe how
     this app may interrupt and hold no personal data. Listeners must already
     exist when a tapped notification opens the app cold — Capacitor emits that
     event once, with no queue, so anything attached later simply misses it. */
  React.useEffect(() => { void initPush(); }, []);

  React.useEffect(() => {
    setPushHandlers({
      /* Arrived while she is already looking at the app. Refresh what is on
         screen and otherwise stay out of the way: Android has posted the
         notification itself, and yanking her to another screen mid-sentence —
         possibly mid-message to the companion — is not something a
         notification has earned. */
      onForeground: () => { void client.invalidateQueries(); },

      /* She tapped it. That is an explicit request to be taken somewhere. */
      onOpened: (n) => {
        const target = routeForNotification(n?.data?.notifType);
        if (target) navigate(target);
      },
    });
  }, [navigate, client]);

  React.useEffect(() => {
    if (isAuthenticated) void registerForPush();
  }, [isAuthenticated]);

  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PushBridge />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={
            <RequireGuest><Suspense><LoginScreen /></Suspense></RequireGuest>
          } />
          <Route path="/register" element={
            <RequireGuest><Suspense><RegisterScreen /></Suspense></RequireGuest>
          } />
          <Route path="/verify-email" element={<Suspense><VerifyEmailScreen /></Suspense>} />
          <Route path="/reset-password" element={<Suspense><LoginScreen /></Suspense>} />

          {/* Crisis screen — always accessible, no auth required */}
          <Route path="/crisis" element={<Suspense><CrisisScreen /></Suspense>} />

          {/* Legal — public, required for App Store */}
          <Route path="/privacy" element={<Suspense><PrivacyPolicyScreen /></Suspense>} />
          <Route path="/terms"   element={<Suspense><TermsScreen /></Suspense>} />

          {/* Protected routes */}
          <Route element={<RequireAuth><RequireSetup><AppLayout /></RequireSetup></RequireAuth>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/onboarding" element={<Suspense><OnboardingScreen /></Suspense>} />
            <Route path="/dashboard" element={<Suspense><DashboardScreen /></Suspense>} />
            <Route path="/assessment/:type?" element={<Suspense><AssessmentScreen /></Suspense>} />
            <Route path="/mood" element={<Suspense><MoodScreen /></Suspense>} />
            <Route path="/companion" element={<Suspense><CompanionScreen /></Suspense>} />
            <Route path="/content/:slug?" element={<Suspense><ContentScreen /></Suspense>} />
            <Route path="/care-plan" element={<Suspense><CarePlanScreen /></Suspense>} />
            <Route path="/profile" element={<Suspense><ProfileScreen /></Suspense>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
